import { Suspense, lazy, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useParams, Outlet } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from './services/AuthContext';
import { ConfigProvider } from './context/ConfigProvider';
import { Helmet } from 'react-helmet-async';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { CurrencyProvider } from './services/CurrencyContext';
import { AuthModalProvider } from './context/AuthModalContext';
import { CountryProvider, useCountry } from './context/CountryContext';
import ScrollToTop from './components/ScrollToTop';
import ErrorBoundary from './components/ErrorBoundary';

import DeveloperTerminalLayout from './layouts/DeveloperTerminalLayout';

const DeveloperDashboardPage = lazy(() => import('./pages/DeveloperDashboardPage'));
const DeveloperLoginPage = lazy(() => import('./pages/DeveloperLoginPage'));
const DeveloperSignupPage = lazy(() => import('./pages/DeveloperSignupPage'));
const CheckEmailPage = lazy(() => import('./pages/CheckEmailPage'));
const VerifyEmailPage = lazy(() => import('./pages/VerifyEmailPage'));
const RequestPasswordResetPage = lazy(() => import('./pages/RequestPasswordResetPage'));
const ResetPasswordPage = lazy(() => import('./pages/ResetPasswordPage'));
const DeveloperInventoryPage = lazy(() => import('./pages/DeveloperInventoryPage'));
const DeveloperSettingsPage = lazy(() => import('./pages/DeveloperSettingsPage'));
const ProfilePage = lazy(() => import('./pages/ProfilePage'));
const InsightsPage = lazy(() => import('./pages/InsightsPage'));
const SalesTerminalPage = lazy(() => import('./pages/SalesTerminalPage'));

const PageLoader = () => (
  <div className="flex h-screen w-full items-center justify-center bg-black">
    <div className="h-12 w-12 animate-spin rounded-full border-b-2 border-accent-blue-600"></div>
  </div>
);

const ProtectedDeveloperRoute = () => {
  const { isLoggedIn, user, isLoading } = useAuth();
  const { countryCode } = useParams<{ countryCode: string }>();
  
  if (isLoading) return <PageLoader />;
  
  if (!isLoggedIn || user?.role !== 'DEVELOPER') {
    return <Navigate to={`/${countryCode || 'eg'}/developers/login`} replace />;
  }
  
  return (
    <DeveloperTerminalLayout>
      <Outlet />
    </DeveloperTerminalLayout>
  );
};

const RootRedirect = () => {
    const { activeCountry } = useCountry();
    if (!activeCountry) return <PageLoader />;
    return <Navigate to={`/${activeCountry.code.toLowerCase()}`} replace />;
};

const RoleBasedHomePage = () => {
    const { user, isLoggedIn, isLoading } = useAuth();
    
    if (isLoading) return <PageLoader />;
    
    if (isLoggedIn && user?.role === 'DEVELOPER') {
        return <Navigate to="developer/dashboard" replace />;
    }
    
    return <Navigate to="developers/login" replace />;
};

function DeveloperPortalLayout() {
  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        <Route path="" element={<RoleBasedHomePage />} />
        
        {/* Developer Auth */}
        <Route path="developers/login" element={<DeveloperLoginPage />} />
        <Route path="developers/signup" element={<DeveloperSignupPage />} />
        
        <Route path="check-email" element={<CheckEmailPage />} />
        <Route path="verify-email" element={<VerifyEmailPage />} />
        <Route path="reset-password-request" element={<RequestPasswordResetPage />} />
        <Route path="reset-password" element={<ResetPasswordPage />} />
        
        {/* --- Developer Terminal Hub --- */}
        <Route element={<ProtectedDeveloperRoute />}>
          <Route path="developer/dashboard" element={<DeveloperDashboardPage />} />
          <Route path="developer/inventory" element={<DeveloperInventoryPage />} />
          <Route path="developer/sales" element={<SalesTerminalPage />} />
          <Route path="developer/insights" element={<InsightsPage />} />
          
          <Route path="developer/projects" element={<Navigate to="../inventory" replace />} />
          <Route path="developer/projects/*" element={<Navigate to="../inventory" replace />} />
          <Route path="developer/pump" element={<Navigate to="../inventory" replace />} />
          <Route path="developer/pump/*" element={<Navigate to="../inventory" replace />} />
          <Route path="developer/analytics" element={<Navigate to="../dashboard" replace />} />
          <Route path="developer/collab" element={<Navigate to="../dashboard" replace />} />

          <Route path="developer/settings" element={<DeveloperSettingsPage />} />
          <Route path="developer/profile" element={<ProfilePage />} />
        </Route>
        
        {/* Catch All inside country */}
        <Route path="*" element={<Navigate to="" replace />} />
      </Routes>
    </Suspense>
  )
}
        
function App() {
  const { i18n } = useTranslation();
  
  useEffect(() => {
    document.documentElement.dir = i18n.language === 'ar' ? 'rtl' : 'ltr';
    document.documentElement.lang = i18n.language;
  }, [i18n.language]);

  return (
    <GoogleOAuthProvider clientId={import.meta.env.VITE_GOOGLE_CLIENT_ID || "YOUR_GOOGLE_CLIENT_ID_HERE"}>
      <AuthModalProvider>
        <ConfigProvider>
          <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
            <CountryProvider>
              <CurrencyProvider>
                <Helmet>
                  <title>Laqy Developer Terminal | Real-Time Sales Feed</title>
                  <meta name="description" content="Access the real-time sales feed and project statuses as a developer partner." />
                </Helmet>
                <ErrorBoundary>
                  <ScrollToTop />
                  <Routes>
                    <Route path="/" element={<RootRedirect />} />
                    <Route path="/:countryCode/*" element={<DeveloperPortalLayout />} />
                    <Route path="*" element={<Navigate to="/" replace />} />
                  </Routes>
                </ErrorBoundary>
              </CurrencyProvider>
            </CountryProvider>
          </Router>
        </ConfigProvider>
      </AuthModalProvider>
    </GoogleOAuthProvider>
  );
}

export default App;
